import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { ChevronDownIcon } from '@mote/assets/icons'
import { VisuallyHidden } from '@mote/ui'

const TRANSCRIPTION_MAX_HEIGHT = 90

export const Transcription = ({
  moteId = '',
  text,
  isQuiescent,
  isLandingPage,
  size = 'md'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const id = `transcript-panel-${moteId}`
  const isStem = typeof text === 'object'
  const transcriptText = isLandingPage ? (
    text
  ) : (
    <>
      {text} {!isQuiescent ? '(still transcribing...)' : ''}
    </>
  )
  const pluginMarkup = text ? (
    <TranscriptionBox aria-label='Transcript Available'>
      <VisuallyHidden>Transcript Available</VisuallyHidden>
      <h4 style={{ margin: 0 }}>
        <TranscriptionTrigger
          type='button'
          size={size}
          aria-label={`${isOpen ? 'Hide' : 'Show'} transcript`}
          aria-expanded={isOpen}
          aria-controls={id}
          onClick={() => setIsOpen(!isOpen)}
        >
          <VisuallyHidden>
            {`${isOpen ? 'Hide' : 'Show'} transcript`}
          </VisuallyHidden>
          <TranscriptionTriggerText>
            {!isOpen ? text : 'Hide transcript'}
          </TranscriptionTriggerText>
          <StyledChevronDownIcon
            width='0.75em'
            height='0.75em'
            color='currentColor'
            aria-hidden={true}
            focusable={false}
            isOpen={isOpen}
          />
        </TranscriptionTrigger>
      </h4>
      <TranscriptionPanelWrapper isOpen={isOpen}>
        <TranscriptionPanel id={id} role='region'>
          <TranscriptionScrollArea size={size} aria-live='polite'>
            {text}
          </TranscriptionScrollArea>
        </TranscriptionPanel>
      </TranscriptionPanelWrapper>
    </TranscriptionBox>
  ) : null

  const landingMarkup = text ? (
    <TranscriptionBox aria-label='Transcript available'>
      <VisuallyHidden>Transcript available</VisuallyHidden>
      <TranscriptionPanel id={id} role='region' isLandingPage>
        <TranscriptionScrollArea size={size} aria-live='polite'>
          {text}
        </TranscriptionScrollArea>
      </TranscriptionPanel>
    </TranscriptionBox>
  ) : null

  const transcriptMarkup = isLandingPage ? landingMarkup : pluginMarkup

  return transcriptMarkup
}

Transcription.propTypes = {
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isQuiescent: PropTypes.bool,
  isLandingPage: PropTypes.bool
}

const TranscriptionBox = styled.div`
  background: #f6f6f9;
  border-radius: 4px;
  width: 100%;
  height: 100%;
`

const TranscriptionTrigger = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  gap: 8px;
  width: 100%;
  border-radius: 4px;
  padding: 0px 8px;
  appearance: none;
  user-select: none;
  background-color: transparent;
  border: 1px solid transparent;
  color: #232323;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition: all 150ms ease-in;

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          height: 28px;
          font-size: 13.5px;
        `
      case 'md':
      default:
        return css`
          height: 32px;
          font-size: 14.5px;
        `
    }
  }};

  &:focus {
    box-shadow: 0 0 0 3px rgba(172, 10, 231, 0.4);
  }
`

const TranscriptionTriggerText = styled.span`
  display: -webkit-box;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  overflow: hidden;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`

const TranscriptionPanelWrapper = styled.div`
  max-height: 0;
  opacity: 0;
  visibility: hidden;
  transition-property: height, max-height, opacity, visibility;
  transition-duration: 150ms;
  transition-timing-function: ease-in;

  ${({ isOpen }) =>
    isOpen &&
    css`
      max-height: ${TRANSCRIPTION_MAX_HEIGHT}px;
      opacity: 1;
      visibility: visible;
    `};
`

const TranscriptionPanel = styled.div`
  padding: 8px;
  overflow: hidden;
  max-height: ${({ isLandingPage = false }) =>
    isLandingPage ? `${TRANSCRIPTION_MAX_HEIGHT}px` : 'inherit'};
  outline: transparent solid 2px !important;
  outline-offset: 2px;
`

const TranscriptionScrollArea = styled.div`
  color: #232323;
  overflow: hidden;
  overflow-y: auto;
  white-space: break-spaces;
  width: 100%;
  max-height: calc(${TRANSCRIPTION_MAX_HEIGHT}px - 16px);
  transition: max-height 150ms ease-in;

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          font-size: 13.5px;
        `
      case 'md':
      default:
        return css`
          font-size: 14.5px;
        `
    }
  }};
`

const StyledChevronDownIcon = styled(ChevronDownIcon)`
  transition: transform 150ms ease-in;
  flex-shrink: 0;

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(-180deg);
    `}
`
